import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';

import { getApiUrl } from '../../utils/apiUtils';
import FeedbackDetailDrawer from './FeedbackDetailDrawer';

const FEEDBACK_TYPES = [
  { value: '', label: 'All types' },
  { value: 'bug', label: 'Bug' },
  { value: 'feedback', label: 'Feedback' },
  { value: 'feature_request', label: 'Feature request' },
  { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'new', label: 'New' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'archived', label: 'Archived' },
];

const ATTACHMENT_FILTER = [
  { value: 'all', label: 'All attachments' },
  { value: 'with', label: 'With attachments' },
  { value: 'without', label: 'Without attachments' },
];

const CONTEXT_FILTER = [
  { value: 'all', label: 'All context' },
  { value: 'with', label: 'Has context' },
  { value: 'without', label: 'No context' },
];

const PAGE_SIZE = 25;

const StatusBadge = ({ value }) => {
  const palette = {
    new: 'bg-blue-500/15 text-blue-200 border-blue-500/40',
    in_progress: 'bg-amber-500/15 text-amber-200 border-amber-500/40',
    resolved: 'bg-emerald-500/15 text-emerald-200 border-emerald-500/40',
    archived: 'bg-gray-500/15 text-gray-300 border-gray-500/40',
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium capitalize ${palette[value] || 'border-gray-600 text-gray-300'}`}>
      {value?.replace('_', ' ') || 'unknown'}
    </span>
  );
};

const FeedbackAnalyticsPanel = ({ stats, onExport }) => {
  const hasData = stats.total > 0;
  const statusEntries = Object.entries(stats.byStatus || {});
  const typeEntries = Object.entries(stats.byType || {});

  return (
    <section className="rounded-2xl border border-gray-900 bg-gray-900/50 p-5 shadow-lg">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-800 pb-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-gray-500">Analytics</p>
          <h2 className="text-lg font-semibold text-white">Current filter insights</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onExport('csv')}
            disabled={!hasData}
            className="rounded-lg border border-gray-800 px-3 py-1.5 text-xs font-medium text-gray-200 hover:border-blue-500 hover:text-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Export CSV
          </button>
          <button
            type="button"
            onClick={() => onExport('json')}
            disabled={!hasData}
            className="rounded-lg border border-gray-800 px-3 py-1.5 text-xs font-medium text-gray-200 hover:border-blue-500 hover:text-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Export JSON
          </button>
        </div>
      </div>
      <div className="mt-5 grid gap-4 lg:grid-cols-4">
        <div className="rounded-xl border border-gray-800 bg-black/20 p-4">
          <p className="text-xs uppercase tracking-wide text-gray-500">Total</p>
          <p className="text-2xl font-semibold text-white">{stats.total}</p>
          <p className="text-xs text-gray-500 mt-1">{stats.withActivity} include activity</p>
        </div>
        <div className="rounded-xl border border-gray-800 bg-black/20 p-4">
          <p className="text-xs uppercase tracking-wide text-gray-500">Context</p>
          <p className="text-2xl font-semibold text-white">{stats.withContext}</p>
          <p className="text-xs text-gray-500 mt-1">+{stats.withAttachments} with attachments</p>
        </div>
        <div className="rounded-xl border border-gray-800 bg-black/20 p-4">
          <p className="text-xs uppercase tracking-wide text-gray-500">Duplicates / Archived</p>
          <p className="text-2xl font-semibold text-white">
            {stats.duplicates} / {stats.archived}
          </p>
          <p className="text-xs text-gray-500 mt-1">Review triage status</p>
        </div>
        <div className="rounded-xl border border-gray-800 bg-black/20 p-4">
          <p className="text-xs uppercase tracking-wide text-gray-500">Status buckets</p>
          <p className="text-2xl font-semibold text-white">{statusEntries.length}</p>
          <p className="text-xs text-gray-500 mt-1">Active statuses represented</p>
        </div>
      </div>
      <div className="mt-6 grid gap-6 md:grid-cols-2">
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Statuses</p>
          <ul className="space-y-2 text-sm text-gray-200">
            {statusEntries.length ? (
              statusEntries.map(([status, count]) => (
                <li key={status} className="flex items-center justify-between rounded-lg border border-gray-900 bg-black/20 px-3 py-2">
                  <span className="capitalize">{status.replace('_', ' ')}</span>
                  <span className="font-semibold text-white">{count}</span>
                </li>
              ))
            ) : (
              <li className="text-gray-500 text-xs">No data for current filters.</li>
            )}
          </ul>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Feedback types</p>
          <ul className="space-y-2 text-sm text-gray-200">
            {typeEntries.length ? (
              typeEntries.map(([type, count]) => (
                <li key={type} className="flex items-center justify-between rounded-lg border border-gray-900 bg-black/20 px-3 py-2">
                  <span className="capitalize">{type.replace('_', ' ')}</span>
                  <span className="font-semibold text-white">{count}</span>
                </li>
              ))
            ) : (
              <li className="text-gray-500 text-xs">No data for current filters.</li>
            )}
          </ul>
        </div>
      </div>
    </section>
  );
};

const FeedbackListPage = () => {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [results, setResults] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    feedback_type: '',
    status: '',
    account_tier: '',
    attachments: 'all',
    context: 'all',
  });
  const [selectedId, setSelectedId] = useState(null);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);

  const fetchList = useCallback(
    async (page = 1) => {
      setLoading(true);
      setError('');

      try {
        const token = await getToken();
        const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });

        if (filters.feedback_type) params.set('feedback_type', filters.feedback_type);
        if (filters.status) params.set('status', filters.status);
        if (filters.account_tier) params.set('account_tier', filters.account_tier);
        if (filters.attachments === 'with') params.set('has_attachments', 'true');
        if (filters.attachments === 'without') params.set('has_attachments', 'false');
        if (filters.context === 'with') params.set('has_context', 'true');
        if (filters.context === 'without') params.set('has_context', 'false');

        const response = await fetch(`${getApiUrl()}/admin/feedback?${params.toString()}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Unable to load feedback list.');
        }

        const payload = await response.json();
        setResults(payload.results || []);
        setPagination(payload.pagination || { page, total: 0 });
        setLastFetchedAt(new Date().toISOString());
      } catch (fetchError) {
        setError(fetchError.message || 'Unexpected error while fetching feedback.');
      } finally {
        setLoading(false);
      }
    },
    [filters, getToken]
  );

  useEffect(() => {
    fetchList(1);
  }, [filters, fetchList]);

  const filteredResults = useMemo(() => {
    if (!searchQuery.trim()) {
      return results;
    }

    const lowered = searchQuery.toLowerCase();
    return results.filter((submission) => {
      return (
        submission.title?.toLowerCase().includes(lowered) ||
        submission.description?.toLowerCase().includes(lowered) ||
        submission.user?.email?.toLowerCase().includes(lowered)
      );
    });
  }, [results, searchQuery]);

  const refresh = () => fetchList(pagination.page || 1);

  const handleFilterChange = (key, value) => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setFilters((previous) => ({
      ...previous,
      [key]: value,
    }));
  };

  const analytics = useMemo(() => {
    const summary = {
      total: filteredResults.length,
      withContext: 0,
      withAttachments: 0,
      withActivity: 0,
      duplicates: 0,
      archived: 0,
      byStatus: {},
      byType: {},
    };

    filteredResults.forEach((submission) => {
      if (submission.flags?.has_context_snapshot) summary.withContext += 1;
      if (submission.flags?.has_attachments) summary.withAttachments += 1;
      if (submission.flags?.has_activity_summary) summary.withActivity += 1;
      if (submission.flags?.is_duplicate) summary.duplicates += 1;
      if (submission.flags?.is_archived) summary.archived += 1;

      if (submission.status) {
        summary.byStatus[submission.status] = (summary.byStatus[submission.status] || 0) + 1;
      }

      if (submission.feedback_type) {
        summary.byType[submission.feedback_type] = (summary.byType[submission.feedback_type] || 0) + 1;
      }
    });

    return summary;
  }, [filteredResults]);

  const handleExport = useCallback(
    (format) => {
      if (!filteredResults.length || typeof document === 'undefined') {
        return;
      }

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

      if (format === 'json') {
        const payload = JSON.stringify(filteredResults, null, 2);
        const blob = new Blob([payload], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `feedback_export_${timestamp}.json`;
        link.click();
        URL.revokeObjectURL(url);
        return;
      }

      const headers = [
        'id',
        'created_at',
        'feedback_type',
        'status',
        'title',
        'account_tier',
        'locale',
        'assigned_to',
        'user_email',
        'has_context_snapshot',
        'has_activity_summary',
        'has_attachments',
        'is_duplicate',
        'is_archived',
      ];

      const rows = filteredResults.map((submission) => [
        submission.id,
        submission.created_at || '',
        submission.feedback_type || '',
        submission.status || '',
        `"${(submission.title || '').replace(/"/g, '""')}"`,
        submission.user?.account_tier || '',
        submission.user?.locale || '',
        submission.assigned_to || '',
        submission.user?.email || '',
        submission.flags?.has_context_snapshot ? 'yes' : 'no',
        submission.flags?.has_activity_summary ? 'yes' : 'no',
        submission.flags?.has_attachments ? 'yes' : 'no',
        submission.flags?.is_duplicate ? 'yes' : 'no',
        submission.flags?.is_archived ? 'yes' : 'no',
      ]);

      const csvContent = [headers.join(','), ...rows.map((cells) => cells.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `feedback_export_${timestamp}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    },
    [filteredResults]
  );

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100">
      <div className="mx-auto w-full max-w-7xl px-6 py-10 space-y-8">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-sm uppercase tracking-widest text-blue-400">Admin</p>
            <h1 className="text-3xl font-semibold text-white">Feedback Inbox</h1>
            <p className="text-sm text-gray-400">
              Triage and manage coaching feedback submissions in real-time.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="inline-flex items-center rounded-lg border border-gray-800 bg-black/30 px-3 py-2 text-xs font-medium text-gray-200 hover:text-white hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Back to Coach
            </button>
            {lastFetchedAt && (
              <span className="text-xs text-gray-500">
                Updated {new Date(lastFetchedAt).toLocaleTimeString(undefined, { timeStyle: 'short' })}
              </span>
            )}
            <button
              type="button"
              onClick={refresh}
              className="inline-flex items-center rounded-lg border border-blue-500/40 bg-blue-500/10 px-4 py-2 text-sm font-medium text-blue-200 hover:bg-blue-500/20 focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              Refresh
            </button>
          </div>
        </header>

        <section className="rounded-2xl border border-gray-900 bg-gray-900/60 p-4 shadow-lg">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
            <div className="lg:col-span-2">
              <label className="text-xs uppercase tracking-wide text-gray-500">Search</label>
              <input
                type="text"
                placeholder="Search title, description, or email"
                className="mt-1 w-full rounded-lg border border-gray-800 bg-black/40 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-gray-500">Type</label>
              <select
                value={filters.feedback_type}
                onChange={(event) => handleFilterChange('feedback_type', event.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-800 bg-black/40 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {FEEDBACK_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-gray-500">Status</label>
              <select
                value={filters.status}
                onChange={(event) => handleFilterChange('status', event.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-800 bg-black/40 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-gray-500">Account tier</label>
              <input
                type="text"
                placeholder="e.g. beta"
                value={filters.account_tier}
                onChange={(event) => handleFilterChange('account_tier', event.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-800 bg-black/40 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-3 lg:col-span-2">
              <div>
                <label className="text-xs uppercase tracking-wide text-gray-500">Attachments</label>
                <select
                  value={filters.attachments}
                  onChange={(event) => handleFilterChange('attachments', event.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-800 bg-black/40 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {ATTACHMENT_FILTER.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-gray-500">Context</label>
                <select
                  value={filters.context}
                  onChange={(event) => handleFilterChange('context', event.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-800 bg-black/40 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {CONTEXT_FILTER.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </section>

        <FeedbackAnalyticsPanel stats={analytics} onExport={handleExport} />

        {error && (
          <div className="rounded-xl border border-red-500/40 bg-red-900/20 p-4 text-sm text-red-100">{error}</div>
        )}

        <section className="rounded-2xl border border-gray-900 bg-gray-900/40 shadow-xl">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-900 text-sm">
              <thead className="bg-gray-900/60">
                <tr className="text-left text-xs uppercase tracking-widest text-gray-400">
                  <th className="px-4 py-3 font-medium">ID</th>
                  <th className="px-4 py-3 font-medium">Created</th>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Title</th>
                  <th className="px-4 py-3 font-medium">Tier</th>
                  <th className="px-4 py-3 font-medium">Locale</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-center">Context</th>
                  <th className="px-4 py-3 font-medium text-center">Attachments</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-900">
                {loading && (
                  <tr>
                    <td colSpan={9} className="px-4 py-10 text-center text-gray-400">
                      Loading feedback…
                    </td>
                  </tr>
                )}
                {!loading && !filteredResults.length && (
                  <tr>
                    <td colSpan={9} className="px-4 py-10 text-center text-gray-500">
                      No feedback found for the selected filters.
                    </td>
                  </tr>
                )}
                {!loading &&
                  filteredResults.map((submission) => (
                    <tr
                      key={submission.id}
                      className="cursor-pointer bg-transparent transition hover:bg-blue-500/5"
                      onClick={() => setSelectedId(submission.id)}
                    >
                      <td className="px-4 py-3 font-mono text-xs text-gray-400">#{submission.id}</td>
                      <td className="px-4 py-3 text-gray-300">
                        {submission.created_at
                          ? new Date(submission.created_at).toLocaleString(undefined, {
                              dateStyle: 'short',
                              timeStyle: 'short',
                            })
                          : '—'}
                      </td>
                      <td className="px-4 py-3 capitalize text-gray-200">{submission.feedback_type}</td>
                      <td className="px-4 py-3 text-white">
                        <p className="font-semibold">{submission.title}</p>
                        <p className="text-xs text-gray-500 truncate max-w-xs">{submission.description}</p>
                      </td>
                      <td className="px-4 py-3 text-gray-300">{submission.user?.account_tier || '—'}</td>
                      <td className="px-4 py-3 text-gray-300">{submission.user?.locale || '—'}</td>
                      <td className="px-4 py-3">
                        <StatusBadge value={submission.status} />
                      </td>
                      <td className="px-4 py-3 text-center">
                        {submission.flags?.has_context_snapshot ? (
                          <span className="text-xs text-emerald-300">Yes</span>
                        ) : (
                          <span className="text-xs text-gray-500">No</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {submission.flags?.has_attachments ? submission.attachment_count : 0}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center justify-between border-t border-gray-900 px-4 py-3 text-xs text-gray-400">
            <p>
              Page {pagination.page} · {pagination.total} total submissions
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => fetchList(Math.max(1, (pagination.page || 1) - 1))}
                disabled={(pagination.page || 1) <= 1 || loading}
                className="rounded-lg border border-gray-800 px-3 py-1 text-gray-300 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => fetchList((pagination.page || 1) + 1)}
                disabled={(pagination.page || 1) * PAGE_SIZE >= (pagination.total || 0) || loading}
                className="rounded-lg border border-gray-800 px-3 py-1 text-gray-300 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </section>
      </div>

      {selectedId && <FeedbackDetailDrawer submissionId={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  );
};

export default FeedbackListPage;

